import React, { useState } from 'react';
import { Modal, Platform, Pressable, Share, StyleSheet, TextInput, View } from 'react-native';
import MessageActions from '@/components/Messages/MessageActions';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { useToastStore } from '@/store';
import copyText from '@/constants/utils/copy_text';
import { saveExportedBlob } from '@/constants/utils/conversationExport';
import { openPlaceLink } from '@/components/Messages/MessagePlaces';
import { placeGuideText } from './placeGuideContent';

export default function PlaceGuideActions({ place, guide, menuContainer, onShareOpenChange }) {
  const { colors } = useTheme();
  const addToast = useToastStore(state => state.actions.addToast);
  const [sharing, setSharing] = useState(false);
  const content = placeGuideText(place, guide);
  const closeShare = () => { setSharing(false); onShareOpenChange?.(false); };
  const copy = async () => {
    try { await copyText(content); addToast('Place guide copied', 'success'); }
    catch (_) { addToast('Could not copy the guide. Please try again.', 'error'); }
  };
  const share = async () => {
    try {
      if (Platform.OS !== 'web') await Share.share({ title: place.title, message: content });
      else { setSharing(true); onShareOpenChange?.(true); }
    } catch (error) {
      if (error?.name !== 'AbortError') addToast('Could not share the guide. Please try again.', 'error');
    }
  };
  const shareToApps = async () => {
    try { await navigator.share({ title: place.title, text: content }); }
    catch (error) {
      if (error?.name !== 'AbortError') addToast('Could not share the guide. Please try again.', 'error');
    }
  };
  const download = async () => {
    try {
      await saveExportedBlob(new Blob([content], { type: 'text/plain;charset=utf-8' }), { title: place.title }, 'text');
      addToast('Place guide downloaded', 'success');
    } catch (_) { addToast('Could not download the guide. Please try again.', 'error'); }
  };
  return <>
    <MessageActions isVisible isUser={false} showDetails={false} menuContainer={menuContainer} onCopy={copy} onShare={share} onExport={download} />
    <Modal visible={sharing} transparent animationType="fade" onRequestClose={closeShare}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFillObject} accessibilityRole="button" accessibilityLabel="Dismiss share guide" onPress={closeShare} />
        <View accessibilityViewIsModal {...(Platform.OS === 'web' ? { role: 'dialog', 'aria-label': 'Share place guide', 'aria-modal': true } : {})} style={[styles.dialog, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary }]}>
          <View style={styles.header}><Text weight="semibold" style={{ fontSize: 21 }}>Share place guide</Text><Pressable accessibilityRole="button" accessibilityLabel="Close share guide" onPress={closeShare} style={styles.close}><Icon name="xmark" size={18} color={colors.text_color_primary} /></Pressable></View>
          <Text size="s" color="secondary">Copy the full guide or open it in an email.</Text>
          <TextInput accessibilityLabel="Place guide to share" multiline editable={false} value={content} style={[styles.preview, { color: colors.text_color_primary, backgroundColor: colors.solid_color_secondary }]} />
          <View style={styles.buttons}>
            <Button text="Copy guide" prependIcon="copy" variant="secondary" onPress={copy} />
            <Button text="Email guide" prependIcon="envelope" variant="secondary" onPress={() => openPlaceLink(`mailto:?subject=${encodeURIComponent(place.title)}&body=${encodeURIComponent(content)}`)} />
            {Platform.OS === 'web' && typeof navigator.share === 'function' && <Button text="Share to apps" prependIcon="arrow-up-from-bracket" variant="secondary" onPress={shareToApps} />}
          </View>
        </View>
      </View>
    </Modal>
  </>;
}

const styles = StyleSheet.create({
  overlay: { flex: 1, padding: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.4)' },
  dialog: { width: '100%', maxWidth: 460, maxHeight: '90%', padding: 24, borderRadius: 24, borderWidth: 1, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  close: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center' },
  preview: { height: 230, flexShrink: 1, minHeight: 80, padding: 16, borderRadius: 14, fontSize: 14, lineHeight: 22, textAlignVertical: 'top' },
  buttons: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
});
